import { useState, useEffect, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";

export default function Service() {
  const { t } = useTranslation();
  const { lang: langParam } = useParams();
  const location = useLocation();

  // زبان از URL (حتی اگر بیرون از Route :lang باشه)
  const seg = location.pathname.split("/")[1];
  const lang = langParam || (seg === "fa" || seg === "en" ? seg : "en");

  // نرمال‌سازی مسیر تصویرها (فرض: public/assets)
  const asset = (p) => {
    if (!p) return "/assets/placeholder.png";
    if (/^https?:\/\//i.test(p)) return p;
    if (p.startsWith("/")) return p;
    return p.replace(/^(\.\.\/)+assets\//, "/assets/");
  };

  // رندر بلوک‌های محتوا از i18n
  const renderBlocks = (blocks) =>
    blocks.map((b, i) => {
      if (b.type === "p") return <p key={i}>{t(b.key)}</p>;
      if (b.type === "h3") return <h3 key={i}>{t(b.key)}</h3>;
      if (b.type === "ul") {
        const items = t(b.key, { returnObjects: true });
        return (
          <ul key={i}>
            {Array.isArray(items) &&
              items.map((li, j) => <li key={j}>{li}</li>)}
          </ul>
        );
      }
      if (b.type === "ol") {
        const items = t(b.key, { returnObjects: true });
        return (
          <ol key={i}>
            {Array.isArray(items) &&
              items.map((li, j) => <li key={j}>{li}</li>)}
          </ol>
        );
      }
      if (b.type === "img")
        return (
          <img
            key={i}
            src={asset(b.src)}
            alt={t(b.altKey)}
            className="img-responsive center-block"
            loading="lazy"
          />
        );
      return null;
    });

  // ——— گروه 1: TXP ———
  const items = useMemo(
    () => [
      {
        id: "txp-advantech",
        title: t("service.txp.advantech.title"),
        blocks: [
          { type: "p", key: "service.txp.advantech.p1" },
          { type: "ul", key: "service.txp.advantech.bullets" },
          { type: "h3", key: "service.txp.advantech.solutionTitle" },
          {
            type: "img",
            src: "../../assets/futoad.png",
            altKey: "service.txp.advantech.imageAlt",
          },
        ],
      },
      {
        id: "txp-app-server",
        title: t("service.txp.appserver.title"),
        blocks: [
          { type: "p", key: "service.txp.appserver.p1" },
          { type: "p", key: "service.txp.appserver.p2" },
          { type: "p", key: "service.txp.appserver.p3" },
          {
            type: "img",
            src: "../../assets/applic.png",
            altKey: "service.txp.appserver.imageAlt",
          },
          { type: "h3", key: "service.txp.appserver.benefitTitle" },
          { type: "ul", key: "service.txp.appserver.benefits" },
        ],
      },
      {
        id: "txp-scalance",
        title: t("service.txp.scalance.title"),
        blocks: [{ type: "ul", key: "service.txp.scalance.bullets" }],
      },
      {
        id: "txp-s5tos7",
        title: t("service.txp.s5tos7.title"),
        blocks: [
          { type: "p", key: "service.txp.s5tos7.p1" },
          { type: "p", key: "service.txp.s5tos7.p2" },
          {
            type: "img",
            src: "../../assets/s5tos7.png",
            altKey: "service.txp.s5tos7.imageAlt",
          },
          { type: "h3", key: "service.txp.s5tos7.planTitle" },
          { type: "p", key: "service.txp.s5tos7.planDesc" },
          { type: "h3", key: "service.txp.s5tos7.featuresTitle" },
          { type: "ol", key: "service.txp.s5tos7.features" },
          { type: "h3", key: "service.txp.s5tos7.stepsTitle" },
          { type: "ul", key: "service.txp.s5tos7.steps" },
        ],
      },
    ],
    [t]
  );

  // ——— گروه 2: ABB ———
  const items1 = useMemo(
    () => [
      {
        id: "abb-splus",
        title: t("service.abb.splus.title"),
        blocks: [
          { type: "h3", key: "service.abb.splus.benefitTitle" },
          { type: "ol", key: "service.abb.splus.benefits" },
          {
            type: "img",
            src: "../../assets/spluse.png",
            altKey: "service.abb.splus.imageAlt",
          },
        ],
      },
      {
        id: "abb-pn800",
        title: t("service.abb.pn800.title"),
        blocks: [
          { type: "p", key: "service.abb.pn800.p1" },
          { type: "h3", key: "service.abb.pn800.benefitTitle" },
          { type: "ul", key: "service.abb.pn800.benefits" },
          { type: "h3", key: "service.abb.pn800.stepsTitle" },
          { type: "ol", key: "service.abb.pn800.steps" },
        ],
      },
    ],
    [t]
  );

  // ——— گروه 3: سایر ———
  const otheritems = useMemo(
    () => [
      {
        id: "datadiod",
        title: t("service.other.datadiod.title"),
        blocks: [
          { type: "p", key: "service.other.datadiod.p1" },
          { type: "ul", key: "service.other.datadiod.specs" },
          {
            type: "img",
            src: "../../assets/datadiod.png",
            altKey: "service.other.datadiod.imageAlt",
          },
          { type: "h3", key: "service.other.datadiod.benefitTitle" },
          { type: "ul", key: "service.other.datadiod.benefits" },
        ],
      },
      {
        id: "historian",
        title: t("service.other.historian.title"),
        blocks: [
          { type: "ul", key: "service.other.historian.features" },
          {
            type: "img",
            src: "../../assets/log1.jpg",
            altKey: "service.other.historian.imageAlt",
          },
          {
            type: "img",
            src: "../../assets/trend1.jpg",
            altKey: "service.other.historian.imageAlt",
          },
          {
            type: "img",
            src: "../../assets/group1.jpg",
            altKey: "service.other.historian.imageAlt",
          },
          {
            type: "img",
            src: "../../assets/mod1.jpg",
            altKey: "service.other.historian.imageAlt",
          },
        ],
      },
      {
        id: "sms",
        title: t("service.other.sms.title"),
        blocks: [
          { type: "p", key: "service.other.sms.p1" },
          { type: "ul", key: "service.other.sms.features" },
          {
            type: "img",
            src: "../../assets/smsserver.png",
            altKey: "service.other.sms.imageAlt",
          },
        ],
      },
      {
        id: "online",
        title: t("service.other.online.title"),
        blocks: [
          { type: "p", key: "service.other.online.p1" },
          {
            type: "img",
            src: "../../assets/monitor.JPG",
            altKey: "service.other.online.imageAlt",
          },
        ],
      },
    ],
    [t]
  );

  // ——— وضعیت آکاردئون‌ها ———
  const [openIndex, setOpenIndex] = useState(null); // گروه 1
  const [openIndex3, setOpenIndex3] = useState(null); // گروه 2
  const [openIndex2, setOpenIndex2] = useState(null); // سایر

  // بازکردن خودکار با ?section=
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const section = sp.get("section"); // txp | abb | datadiod | historian | sms | online
    if (!section) return;

    if (section === "txp") {
      setOpenIndex(0);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (section === "abb") {
      setOpenIndex3(0);
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
      return;
    }

    const idx = otheritems.findIndex((it) => it.id === section);
    if (idx !== -1) {
      setOpenIndex2(idx);
      setTimeout(() => {
        const el = document.querySelector(
          `.acbox:nth-of-type(3) .accordion:nth-of-type(${idx + 1})`
        );
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [location.search, otheritems]);

  return (
    <>
      <Helmet>
        <title>{t("service.seo.title")}</title>
        <meta name="description" content={t("service.seo.description")} />
        <meta name="keywords" content={t("service.seo.keywords")} />
        <link
          rel="alternate"
          hrefLang="fa"
          href={`${window.location.origin}/fa/service`}
        />
        <link
          rel="alternate"
          hrefLang="en"
          href={`${window.location.origin}/en/service`}
        />
      </Helmet>

      <div className="container pt-3 pb-5" dir={lang === "en" ? "ltr" : "rtl"}>
        <h1 className="servicep text-blue1 mt-3 mb-2 fs-6">
          {t("service.pageTitle")}
        </h1>

        {/* گروه 1 */}
        <div className="acbox">
          <h3 className="text-blue2 mt-5 mb-2 fs-5">
            {t("service.groups.txp")}
          </h3>
          {items.map((it, i) => (
            <div key={it.id}>
              <button
                className={`accordion ${openIndex === i ? "active" : ""}`}
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                {it.title}
                <span className="arrow">
                  <img
                    src="/assets/arrowwdown.png"
                    alt="arrow"
                    className={openIndex === i ? "rotated" : ""}
                  />
                </span>
              </button>
              {openIndex === i && (
                <div className="panel">{renderBlocks(it.blocks)}</div>
              )}
            </div>
          ))}
        </div>

        {/* گروه 2 */}
        <div className="acbox">
          <h3 className="text-blue2 mt-5 mb-2 fs-5">
            {t("service.groups.abb")}
          </h3>
          {items1.map((it, i) => (
            <div key={it.id}>
              <button
                className={`accordion ${openIndex3 === i ? "active" : ""}`}
                onClick={() => setOpenIndex3(openIndex3 === i ? null : i)}
              >
                {it.title}
                <span className="arrow">
                  <img
                    src="/assets/arrowwdown.png"
                    alt="arrow"
                    className={openIndex3 === i ? "rotated" : ""}
                  />
                </span>
              </button>
              {openIndex3 === i && (
                <div className="panel">{renderBlocks(it.blocks)}</div>
              )}
            </div>
          ))}
        </div>

        {/* گروه 3 */}
        <h2 className="text-blue1 mt-5 mb-2 fs-6 serviceother">
          {t("service.groups.other")}
        </h2>
        <div className="acbox">
          {otheritems.map((it, i) => (
            <div key={it.id}>
              <button
                className={`accordion ${openIndex2 === i ? "active" : ""}`}
                onClick={() => setOpenIndex2(openIndex2 === i ? null : i)}
              >
                {it.title}
                <span className="arrow">
                  <img
                    src="/assets/arrowwdown.png"
                    alt="arrow"
                    className={openIndex2 === i ? "rotated" : ""}
                  />
                </span>
              </button>
              {openIndex2 === i && (
                <div className="panel">{renderBlocks(it.blocks)}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
